import {
  ExternalAccountInvitationWithKey,
  LanguageNameSpaces,
  UserImpersonationPermissionLevel,
} from "@shared/types";
import Row from "antd/es/row";
import Col from "antd/es/col";
import ExpandingCard from "common/Boxes/ExpandingCard.tsx";
import React from "react";
import { INVITATION_CARD_COLUMN_SPANS } from "features/configuration/InviteExternalUsers/constants.ts";
import styled from "styled-components";
import Text from "common/Typography/Text";
import { formatDate } from "@shared/utils/date";
import InvitationStatusTag from "features/configuration/InviteExternalUsers/InvitationStatusTag.tsx";
import {
  useShowPermissionOptions,
  useInvitationActions,
} from "features/configuration/InviteExternalUsers/hooks.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { INVITATION_PERMISSION_TO_DISPLAY } from "features/configuration/common/constants.ts";

interface InvitationCardProps {
  invitation: ExternalAccountInvitationWithKey;
  isExpanded?: boolean;
  toggleExpanded: (key: string) => void;
}

const InvitationCard = ({
  invitation,
  toggleExpanded,
  isExpanded,
}: InvitationCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { actionsItems } = useInvitationActions(invitation);
  const { showClientPermissionOptions, showAssetManagerPermissionOptions } =
    useShowPermissionOptions();

  const getCollapsibleContent = () => {
    return (
      <div>
        <CollapsibleRow>
          <Text type="secondary">{t("invitations.creation_date")}</Text>
          <Text>{formatDate(invitation.creation_datetime, true, true)}</Text>
        </CollapsibleRow>
        <CollapsibleRow>
          <Text type="secondary">{t("invitations.expiration_date")}</Text>
          <Text>{formatDate(invitation.valid_to, true, true)}</Text>
        </CollapsibleRow>
        {showClientPermissionOptions && (
          <CollapsibleRow>
            <Text type="secondary">{t("invitations.client_access")}</Text>
            <Text>
              {
                INVITATION_PERMISSION_TO_DISPLAY[
                  invitation.permission_level ??
                    UserImpersonationPermissionLevel.NONE
                ]
              }
            </Text>
          </CollapsibleRow>
        )}
        {showAssetManagerPermissionOptions && (
          <CollapsibleRow>
            <Text type="secondary">{t("invitations.manager_access")}</Text>
            <Text>
              {
                INVITATION_PERMISSION_TO_DISPLAY[
                  invitation.asset_manager_permission_level ??
                    UserImpersonationPermissionLevel.NONE
                ]
              }
            </Text>
          </CollapsibleRow>
        )}
        <ActionsWrapper>
          {actionsItems.map((item) => {
            return (
              <ActionWrapper
                key={item.key}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  item.onClick();
                }}
              >
                {item.icon}
                {item.label}
              </ActionWrapper>
            );
          })}
        </ActionsWrapper>
      </div>
    );
  };

  return (
    <ExpandingCard
      collapsibleContent={getCollapsibleContent()}
      expanded={isExpanded}
      setExpanded={() => toggleExpanded?.(invitation.key)}
      style={{ padding: "8px 12px" }}
    >
      <Row style={{ alignItems: "center" }}>
        <Col span={INVITATION_CARD_COLUMN_SPANS[0]}>{invitation.name}</Col>
        <Col
          span={INVITATION_CARD_COLUMN_SPANS[1]}
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <InvitationStatusTag invitation={invitation} />
        </Col>
      </Row>
    </ExpandingCard>
  );
};

export default React.memo(InvitationCard);

const CollapsibleRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
`;
const ActionsWrapper = styled.div`
  border-top: ${(p) => p.theme.borders.lightest};
  margin-top: 8px;
  padding-top: 16px;
  display: flex;
`;
const ActionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  color: ${(p) => p.theme.colors.secondaryGrey};
  row-gap: 4px;
  svg {
    font-size: 20px;
  }
`;
